"use client";

import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Lock, Sparkles, X } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import useToastNotification from "@/hook/useToastNotification";
import { useAppDispatch } from "@/store/hooks";
import { LeadPlatform, submitLead } from "@/store/feature/enquiry/leadApi";

interface AnonymousRouteLeadModalProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	isAnonymous: boolean;
	platform: LeadPlatform;
	source: string;
	contactButtonText?: string;
	onClose?: () => void;
	onContactSuccess?: () => void;
	data?: Record<string, unknown>;
}

const normalizePhoneNumber = (phone: string) => phone.replace(/[^\d+]/g, "");
const isValidPhoneNumber = (phone: string) => /^\+?[1-9]\d{6,14}$/.test(phone);
const isValidFullName = (name: string) =>
	/^[A-Za-z]+(?:[ ][A-Za-z]+)*$/.test(name);

const splitFullName = (name: string) => {
	const trimmedName = name.trim().replace(/\s+/g, " ");
	const parts = trimmedName.split(" ");

	if (parts.length === 1) {
		return {
			firstName: parts[0],
			lastName: "",
		};
	}

	return {
		firstName: parts.slice(0, -1).join(" "),
		lastName: parts[parts.length - 1],
	};
};

const AnonymousRouteLeadModal = ({
	isOpen,
	setIsOpen,
	isAnonymous,
	platform,
	source,
	contactButtonText = "Join Now",
	onClose,
	onContactSuccess,
	data,
}: AnonymousRouteLeadModalProps) => {
	const dispatch = useAppDispatch();
	const { showToast } = useToastNotification();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const [fullName, setFullName] = useState("");
	const [fullNameError, setFullNameError] = useState("");
	const [phone, setPhone] = useState("");
	const [phoneError, setPhoneError] = useState("");
	const [isSubmittingLead, setIsSubmittingLead] = useState(false);

	const handleDismiss = () => {
		setIsOpen(false);
		onClose?.();
	};

	const submitLeadFromModal = async () => {
		const normalizedFullName = fullName.trim().replace(/\s+/g, " ");
		const normalizedPhone = normalizePhoneNumber(phone.trim());

		if (!normalizedFullName) {
			setFullNameError("Full name is required");
			return;
		}

		if (!isValidFullName(normalizedFullName)) {
			setFullNameError("Please enter a valid full name");
			return;
		}

		if (!normalizedPhone) {
			setPhoneError("Phone number is required");
			return;
		}

		if (!isValidPhoneNumber(normalizedPhone)) {
			setPhoneError("Please enter a valid phone number");
			return;
		}

		const { firstName, lastName } = splitFullName(normalizedFullName);

		setFullNameError("");
		setPhoneError("");
		setIsSubmittingLead(true);

		try {
			await dispatch(
				submitLead({
					phone: normalizedPhone,
					platform,
					data: {
						source,
						path: pathname,
						query: searchParams.toString(),
						firstName,
						lastName,
						...(data || {}),
					},
				}),
			).unwrap();

			showToast("Thanks! Our team will reach out soon.", "success");
			setFullName("");
			setPhone("");
			setIsOpen(false);
			onContactSuccess?.();
		} catch (error) {
			console.error("Lead submit failed:", error);
			showToast("Failed to capture lead. Please try again.", "error");
		} finally {
			setIsSubmittingLead(false);
		}
	};

	if (!isAnonymous || !isOpen) return null;

	return (
		<div className="ff_poppins fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 p-0 px-3 py-5 sm:px-6 animate-fade-in">
			<div className="relative w-full max-w-[420px] max-h-[92dvh] overflow-y-auto rounded-[24px] border border-gray-100 bg-white shadow-[0px_8px_32px_0px_#0000001A] animate-slide-up">
				<button
					onClick={handleDismiss}
					className="absolute right-4 top-4 z-20 text-white/80 hover:text-white">
					<X className="h-5 w-5" />
				</button>

				<div className="relative overflow-hidden rounded-t-[24px] bg-[linear-gradient(105deg,#2563EB_0%,#5B42F3_55%,#A21CAF_100%)] px-6 pb-8 pt-7 text-center text-white">
					<div className="pointer-events-none absolute -left-10 bottom-[-30px] size-28 rounded-full bg-white/10" />
					<div className="pointer-events-none absolute -right-8 top-[-20px] size-24 rounded-full bg-white/10" />
					<div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
						<Lock className="h-7 w-7 text-white" />
					</div>
					<h3 className="text-[30px] font-semibold leading-[1.15] tracking-[-0.01em] sm:text-[34px]">
						Stay Ahead of the Curve
					</h3>
					<p className="mx-auto mt-3 max-w-[320px] text-[13px] leading-6 text-white/95 sm:text-[14px]">
						Get the latest education news, deadline alerts, and program
						launches.
					</p>
				</div>

				<div className="flex flex-col gap-5 px-5 pb-6 pt-5 sm:px-6">
					<div className="rounded-[14px] border border-[#D7E4FF] bg-[#EFF4FF] p-4">
						<div className="flex items-start gap-3">
							<Sparkles className="mt-0.5 h-5 w-5 text-[#2563EB]" />
							<div>
								<p className="text-[15px] font-semibold leading-[1.35] text-[#1E3A8A]">
									Why join our community?
								</p>
								<p className="mt-2 text-[13px] leading-6 text-[#2563EB]">
									We&apos;ll send you curated updates and reminders so you never
									miss an application window.
								</p>
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<label className="text-[15px] font-medium text-[#1F2937]">
							Full Name <span className="text-[#E11D48]">*</span>
						</label>
						<input
							type="text"
							value={fullName}
							onChange={(e) => {
								setFullName(e.target.value);
								if (fullNameError) setFullNameError("");
							}}
							onPaste={(e) => e.preventDefault()}
							onCopy={(e) => e.preventDefault()}
							onCut={(e) => e.preventDefault()}
							placeholder="Enter your full name"
							className="h-[56px] rounded-[14px] border border-[#CBD5E1] px-5 text-[15px] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#4F46E5] focus:outline-none"
						/>
						{fullNameError && (
							<p className="text-[12px] text-[#CD2C2C] ff_poppins">
								{fullNameError}
							</p>
						)}
					</div>

					<div className="flex flex-col gap-2">
						<label className="text-[15px] font-medium text-[#1F2937]">
							Phone Number <span className="text-[#E11D48]">*</span>
						</label>
						<PhoneInput
							className="w-full rounded-[14px] border border-[#CBD5E1] ff_poppins"
							forceDialCode
							defaultCountry="in"
							hideDropdown={true}
							inputClassName="!h-[56px] !w-full !rounded-[14px] !text-[15px] ff_poppins !text-[#111827] placeholder:!text-[#9CA3AF]"
							countrySelectorStyleProps={{
								buttonClassName:
									"!h-[56px] !bg-white !border-none !rounded-none !px-3 ff_poppins",
							}}
							value={phone}
							onChange={(value) => {
								setPhone(value);
								if (phoneError) setPhoneError("");
							}}
							inputProps={{
								placeholder: "Enter your phone number",
								onPaste: (e) => e.preventDefault(),
								onCopy: (e) => e.preventDefault(),
								onCut: (e) => e.preventDefault(),
							}}
						/>
						{phoneError && (
							<p className="text-[12px] text-[#CD2C2C] ff_poppins">
								{phoneError}
							</p>
						)}
					</div>

					<button
						onClick={submitLeadFromModal}
						disabled={isSubmittingLead}
						className="h-[56px] w-full rounded-[14px] bg-[linear-gradient(95deg,#2563EB_0%,#7C3AED_58%,#A21CAF_100%)] text-[17px] font-semibold text-white shadow-[0px_10px_24px_0px_#4F46E533] transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:hover:scale-100">
						{isSubmittingLead ? "Submitting..." : contactButtonText}
					</button>

					<p className="px-2 text-center text-[11px] leading-6 text-[#6B7280]">
						By joining, you agree to receive updates via SMS/WhatsApp
					</p>
				</div>
			</div>
		</div>
	);
};

export default AnonymousRouteLeadModal;
